import {
  EMPTY_BINNED_DATA_BUFFER,
  GeckoBinnedDataBuffer,
} from 'features/dataLayers/geckoBinnedDataBuffer'
import { useCallback, useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import { GeckoComponentDataFrame, GeckoComponentRect } from 'types'

import { useCdfDataFetcher } from './useCdfDataFetcher'

/**
 * Returns the raw numeric array associated with a given CDF.
 *
 * WARNING: As this returns a 1-dimensional array with no associated
 * measurement units, it's almost always preferable to pick useCdfBinReading()
 * or useCdfBinReadings() instead.
 *
 * Note that NaN represents null/undefined/otherwise missing values.
 * @param cdf             The component data frame to pull data from
 * @param cropSelection An optional x/y area to select data from.

 */
export const useCdfRawValues = (
  cdf: GeckoComponentDataFrame | undefined,
  cropSelection?: GeckoComponentRect
): {
  buffer: GeckoBinnedDataBuffer | undefined
  isLoading: boolean
} => {
  const cdfDataFetcherBase = useCdfDataFetcher()
  const cdfDataFetcherNoParams = useCallback(
    async () => ({
      buffer: cdf ? await cdfDataFetcherBase(cdf, cropSelection) : undefined,
      id: cdf?.id,
      generation: cdf?.generation,
    }),
    [cdf, cdfDataFetcherBase, cropSelection]
  )

  const { data, isLoading: isLoading } = useSWRImmutable(
    // This '1d' is needed so SWR doesn't conflate the 1d and 2d raw-value fetch keys
    [cdf?.id, cdf?.generation, '1d'],
    cdfDataFetcherNoParams,
    // There is no cheap way to compare CDF data to know if it has changed,
    // and SWR will attempt to use stableHash... which involves iterating over
    // all elements in the returned array. Very slow. For more information,
    // see: https://github.com/vercel/swr/issues/1719
    //
    // Instead, check if the cdf-id or generation has changed.
    {
      compare: (a, b) =>
        (a?.id ?? '') + (a?.generation?.toString() ?? '') ===
        (b?.id ?? '') + (b?.generation?.toString() ?? ''),
    }
  )

  return useMemo(
    () => ({
      buffer: data?.buffer ?? EMPTY_BINNED_DATA_BUFFER,
      isLoading: isLoading,
    }),
    [data, isLoading]
  )
}
